import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Checkbox,
  Descriptions,
  Input,
  Modal,
  Space,
  Switch,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";

import { IPond } from "pages/Ponds/types";
import { disableEventBus, enableEventBus } from "http/userApi";
import Button from "common/Button";
import { useLogin } from "hooks/useLogin";
import {
  EVENT_SOURCE_LABELS,
  type EventBusEventSource,
  checkboxStateFromApi,
  formatEventSourcesForDisplay,
  getEventBridgeSourcesFromParams,
  normalizeEventSourcesForApi,
} from "./eventBusSources";
import { isValidEventBusArn } from "./eventBusArn";

import "../StaticSite/style.scss";

const { Text } = Typography;

const FEATURE_DESCRIPTION =
  "Forward pond events to a custom Amazon EventBridge bus in your AWS account. " +
  "Turn on below, then Save. After saving, use Edit to change ARN or sources. " +
  "Turn off forwarding to remove the configuration (API delete). " +
  "The bus resource policy must allow Data Pond to publish events.";

const ARN_INVALID_MSG =
  "Use format: arn:aws:events:region:12-digit-account:event-bus/name";

type LocalSaved = { arn: string; sources: EventBusEventSource[] };

type IProps = {
  bucket: IPond;
  onFetchBucket: () => Promise<unknown>;
  loading: boolean;
  onSetLoading: (value: boolean) => void;
};

const EventBusForwarding = ({
  bucket,
  onFetchBucket,
  loading,
  onSetLoading,
}: IProps) => {
  const msalInstance = useMsal();
  const { showAuthorizationErrorModal } = useLogin(msalInstance);

  const [draftOpen, setDraftOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localSaved, setLocalSaved] = useState<LocalSaved | null>(null);
  const [arnInput, setArnInput] = useState("");
  const [arnError, setArnError] = useState(false);
  const [selectedSources, setSelectedSources] = useState<EventBusEventSource[]>(
    () => [...checkboxStateFromApi(undefined)],
  );

  const configuredArn = useMemo(() => {
    const v = bucket.params.event_bridge_bus_arn;
    return v && String(v).trim() ? String(v).trim() : "";
  }, [bucket.params.event_bridge_bus_arn]);

  /** Bucket can lag after POST; keep UI/delete working until GET includes the ARN */
  const effectiveArn = useMemo(
    () => configuredArn || (localSaved?.arn?.trim() ?? ""),
    [configuredArn, localSaved],
  );

  const roleArn = useMemo(() => {
    const v = bucket.params.event_bridge_role_arn;
    return v && String(v).trim() ? String(v).trim() : "";
  }, [bucket.params.event_bridge_role_arn]);

  const sourcesForReadOnly = useMemo(() => {
    if (configuredArn) return getEventBridgeSourcesFromParams(bucket.params);
    return localSaved?.sources;
  }, [configuredArn, bucket.params, localSaved]);

  const hasSavedConfig = !!effectiveArn;
  const forwardingEnabled = hasSavedConfig || draftOpen;
  const isSetupDraft = draftOpen && !hasSavedConfig;
  const showEditorFields = isSetupDraft || (hasSavedConfig && isEditing);

  useEffect(() => {
    if (configuredArn) {
      setLocalSaved(null);
    }
  }, [configuredArn]);

  useEffect(() => {
    if (isEditing && hasSavedConfig) return;
    setArnInput(effectiveArn);
    setSelectedSources(checkboxStateFromApi(sourcesForReadOnly));
    setArnError(false);
  }, [effectiveArn, sourcesForReadOnly, isEditing, hasSavedConfig]);

  const canConfigure = useMemo(
    () =>
      bucket.management_permissions.can_configure_event_bus ??
      bucket.management_permissions.can_toggle_static_website,
    [bucket.management_permissions],
  );

  const labelStyle = useMemo(
    () => ({
      width: 170,
      color: "rgba(0, 0, 0, 0.45)",
    }),
    [],
  );

  const handleCopyArn = useCallback(() => {
    if (!effectiveArn) return;
    void navigator.clipboard.writeText(effectiveArn);
    message.success({ content: "ARN copied", duration: 2 });
  }, [effectiveArn]);

  const handleSwitch = useCallback(
    (checked: boolean) => {
      if (!canConfigure) return;

      if (checked) {
        const savedNow = !!(configuredArn || localSaved?.arn?.trim());
        if (savedNow) {
          setDraftOpen(false);
          setIsEditing(false);
        } else {
          setDraftOpen(true);
        }
        setArnInput(configuredArn || localSaved?.arn || "");
        setSelectedSources(
          checkboxStateFromApi(
            configuredArn
              ? getEventBridgeSourcesFromParams(bucket.params)
              : localSaved?.sources,
          ),
        );
        setArnError(false);
        return;
      }

      if (effectiveArn) {
        Modal.confirm({
          title: "Remove event bus configuration?",
          content:
            "This calls the API to delete forwarding. Events will no longer be sent to your bus until you configure it again.",
          okText: "Remove",
          okType: "danger",
          onOk: async () => {
            onSetLoading(true);
            const result = await disableEventBus(bucket.name, msalInstance);

            if (result.error === "token expired") {
              onSetLoading(false);
              showAuthorizationErrorModal();
              return Promise.reject();
            }

            if (result.error) {
              message.error({ content: result.error, duration: 4 });
              onSetLoading(false);
              return Promise.reject();
            }

            await onFetchBucket();
            message.success({
              content: "Event bus forwarding disabled",
              duration: 2,
            });
            setDraftOpen(false);
            setIsEditing(false);
            setLocalSaved(null);
            setArnInput("");
            setArnError(false);
            onSetLoading(false);
          },
        });
        return;
      }

      setDraftOpen(false);
      setIsEditing(false);
      setArnInput("");
      setArnError(false);
    },
    [
      bucket.name,
      bucket.params,
      canConfigure,
      configuredArn,
      effectiveArn,
      localSaved,
      msalInstance,
      onFetchBucket,
      onSetLoading,
      showAuthorizationErrorModal,
    ],
  );

  const handleCancelDraft = useCallback(() => {
    setDraftOpen(false);
    setLocalSaved(null);
    setArnInput(configuredArn || "");
    setSelectedSources(
      checkboxStateFromApi(
        getEventBridgeSourcesFromParams(bucket.params) ?? undefined,
      ),
    );
    setArnError(false);
  }, [configuredArn, bucket.params]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setArnInput(effectiveArn);
    setSelectedSources(checkboxStateFromApi(sourcesForReadOnly));
    setArnError(false);
  }, [effectiveArn, sourcesForReadOnly]);

  const handleEnterEdit = useCallback(() => {
    setIsEditing(true);
    setArnInput(effectiveArn);
    setSelectedSources(checkboxStateFromApi(sourcesForReadOnly));
    setArnError(false);
  }, [effectiveArn, sourcesForReadOnly]);

  const handleSave = useCallback(async () => {
    const trimmed = arnInput.trim();
    if (!trimmed) {
      message.warning({
        content: "Enter an event bus ARN",
        duration: 3,
      });
      setArnError(true);
      return;
    }

    if (!isValidEventBusArn(trimmed)) {
      message.error({ content: ARN_INVALID_MSG, duration: 5 });
      setArnError(true);
      return;
    }

    setArnError(false);

    const event_sources = normalizeEventSourcesForApi(selectedSources);
    if (!event_sources.length) {
      message.warning({
        content: "Select at least one event source",
        duration: 3,
      });
      return;
    }

    onSetLoading(true);

    const result = await enableEventBus(
      bucket.name,
      {
        event_bridge_bus_arn: trimmed,
        event_sources,
      },
      msalInstance,
    );

    if (result.error === "token expired") {
      onSetLoading(false);
      showAuthorizationErrorModal();
      return;
    }

    if (result.error) {
      message.error({ content: result.error, duration: 4 });
      onSetLoading(false);
      return;
    }

    setLocalSaved({ arn: trimmed, sources: event_sources });
    await onFetchBucket();
    message.success({
      content: "Saved",
      duration: 2,
    });
    setDraftOpen(false);
    setIsEditing(false);
    onSetLoading(false);
  }, [
    arnInput,
    bucket.name,
    msalInstance,
    onFetchBucket,
    onSetLoading,
    showAuthorizationErrorModal,
    selectedSources,
  ]);

  const cardExtra =
    canConfigure && forwardingEnabled ? (
      <Space size="middle" className="event-bus-forwarding-card-extra">
        {isSetupDraft ? (
          <Button
            className="cancel-btn"
            onClick={handleCancelDraft}
            disabled={loading}
          >
            Cancel
          </Button>
        ) : null}
        {hasSavedConfig && isEditing ? (
          <Button
            className="cancel-btn"
            onClick={handleCancelEdit}
            disabled={loading}
          >
            Cancel
          </Button>
        ) : null}
        {showEditorFields ? (
          <Button
            type="primary"
            onClick={() => void handleSave()}
            disabled={loading}
          >
            Save
          </Button>
        ) : (
          <Button type="primary" onClick={handleEnterEdit} disabled={loading}>
            Edit
          </Button>
        )}
      </Space>
    ) : null;

  const readOnlySourcesLabel = formatEventSourcesForDisplay(
    sourcesForReadOnly ?? undefined,
  );

  return (
    <Card
      title="Custom EventBridge bus"
      className="static-card event-bus-forwarding-card"
      extra={cardExtra}
      bodyStyle={{ padding: 0 }}
    >
      <div className="static-card-descr">{FEATURE_DESCRIPTION}</div>
      <div className="static-card-body event-bus-forwarding">
        <Descriptions column={1} className="event-bus-forwarding-switch-row">
          <Descriptions.Item label="Enable forwarding" labelStyle={labelStyle}>
            <Switch
              checked={forwardingEnabled}
              loading={loading}
              onChange={handleSwitch}
              disabled={!canConfigure}
            />
          </Descriptions.Item>
        </Descriptions>

        {!forwardingEnabled ? (
          <Text type="secondary" className="event-bus-forwarding-empty">
            Not configured. Turn the switch on to set the event bus ARN and
            sources.
          </Text>
        ) : (
          <div className="event-bus-forwarding-form">
            <div className="event-bus-forwarding-field">
              <span className="event-bus-forwarding-field__label">
                Event bus ARN
              </span>
              <div className="event-bus-forwarding-field__control">
                {showEditorFields ? (
                  <>
                    <Input
                      className={
                        arnError ? "event-bus-forwarding-input--error" : ""
                      }
                      placeholder="arn:aws:events:eu-central-1:123456789012:event-bus/my-bus"
                      value={arnInput}
                      onChange={e => {
                        setArnInput(e.target.value);
                        if (arnError) setArnError(false);
                      }}
                      disabled={loading}
                    />
                    {arnError ? (
                      <Text type="danger" className="event-bus-forwarding-hint">
                        {ARN_INVALID_MSG}
                      </Text>
                    ) : null}
                  </>
                ) : (
                  <div>
                    <span className="event-bus-forwarding-readonly-arn">
                      {effectiveArn}
                    </span>
                    {effectiveArn ? (
                      <CopyOutlined
                        className="copy-icon"
                        onClick={handleCopyArn}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <div className="event-bus-forwarding-field">
              <span className="event-bus-forwarding-field__label">
                Event sources
              </span>
              {showEditorFields ? (
                <Checkbox.Group
                  className="event-bus-forwarding-sources"
                  value={selectedSources}
                  onChange={values =>
                    setSelectedSources(values as EventBusEventSource[])
                  }
                  disabled={loading}
                  options={[
                    { label: EVENT_SOURCE_LABELS.s3, value: "s3" },
                    {
                      label: EVENT_SOURCE_LABELS.storage_gateway,
                      value: "storage_gateway",
                    },
                  ]}
                />
              ) : (
                <Text>{readOnlySourcesLabel}</Text>
              )}
            </div>

            {!showEditorFields && roleArn ? (
              <div className="event-bus-forwarding-field">
                <span className="event-bus-forwarding-field__label">
                  Data Pond role ARN
                  <Tooltip
                    title="Add this IAM Role ARN to the resource-based policy (trust) of your EventBridge bus so that Data Pond can publish events to it."
                    placement="top"
                  >
                    <InfoCircleOutlined className="event-bus-forwarding-field__info" />
                  </Tooltip>
                </span>
                <div className="event-bus-forwarding-field__control">
                  <span className="event-bus-forwarding-readonly-arn">
                    {roleArn}
                  </span>
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => {
                      void navigator.clipboard.writeText(roleArn);
                      message.success({
                        content: "Role ARN copied",
                        duration: 2,
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
};

export default memo(EventBusForwarding);
